/**
 * Dynamic Sidebar Component
 *
 * Sidebar navigation that loads menu items from database via API.
 * Supports hierarchical menus with expand/collapse.
 *
 * @since 2025-12-16
 * @epic INIT-0045, INIT-0046
 */

import { useCallback, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useUIStore } from '@/store';
import { useSidebarMenu } from '@/hooks/useMenu';
import { NavMenuItem } from '@/types/menu';
import { MenuItem } from './MenuItem';
import { Spinner } from '@/components/ui/Spinner';
import { useTheme } from '@/theme/ThemeContext';
import { useClientSetting } from '@/hooks/useClientConfig';
import { useAuth } from '@/contexts/AuthContext';
import { ActiveMenuContext, findActiveMenuId, findAncestorIds } from './activeMenu';

/**
 * [TMF9-54372] Paths de itens de menu restritos a superadmin.
 *
 * O item raiz "Configurações" (e seus filhos) reúne ajustes sensíveis ao
 * sistema (integrações, ACL, parâmetros). É filtrado no Sidebar quando
 * o usuário não é superuser. Backend complementa com require_superuser nas
 * rotas correspondentes (/api/v1/settings, /api/v1/integrations, /api/v1/etl).
 */
const SUPERUSER_ONLY_MENU_PATHS = new Set<string>(['/settings']);

/**
 * Roles consideradas superadmin no frontend.
 *
 * Espelha a lista usada em FragmentGate/Header/Footer e o critério aplicado
 * pelo backend ao gravar o claim is_superuser no JWT (admin/superadmin).
 */
const SUPERADMIN_ROLES = [
  'admin',
  'superadmin',
  'ADMIN',
  'SUPERADMIN',
  'administrator',
  'Sadin',
  'sadin',
];

/**
 * Remove recursivamente itens de menu marcados como restritos a superadmin.
 *
 * Compara apenas pelo `path` (estável entre i18n / linguas). Mantém a árvore
 * intacta para todos os outros itens.
 */
function filterSuperuserOnly(
  items: NavMenuItem[],
  isSuperuser: boolean
): NavMenuItem[] {
  if (isSuperuser) return items;
  return items
    .filter((item) => !item.path || !SUPERUSER_ONLY_MENU_PATHS.has(item.path))
    .map((item) =>
      item.children && item.children.length > 0
        ? { ...item, children: filterSuperuserOnly(item.children, isSuperuser) }
        : item
    );
}

/**
 * Cria item de menu de fallback com valores padrão.
 */
const createFallbackItem = (
  id: number,
  label: string,
  path: string,
  icon: string,
  children: NavMenuItem[] = []
): NavMenuItem => ({
  id,
  label,
  path,
  icon,
  target: '_self',
  isExternal: false,
  isLegacy: false,
  isGroup: children.length > 0,
  children,
  level: 0,
});

/**
 * Sub-itens obrigatórios do budget (sempre devem existir).
 */
const BUDGET_CHILDREN: NavMenuItem[] = [
  { ...createFallbackItem(81, 'Orçamentos', '/budget', 'bi-list'), level: 1 },
  { ...createFallbackItem(82, 'Novo Orçamento', '/budget/create', 'bi-plus'), level: 1 },
  { ...createFallbackItem(85, 'Criar Ação', '/budget/actions/create', 'bi-lightning'), level: 1 },
  { ...createFallbackItem(83, 'Centros de Custo', '/budget/cost-centers', 'bi-diagram-3'), level: 1 },
  { ...createFallbackItem(84, 'Extrato', '/budget/statement', 'bi-receipt'), level: 1 },
];

/**
 * Itens de fallback quando a API não está disponível.
 * Garante navegação mínima funcional.
 */
const FALLBACK_MENU_ITEMS: NavMenuItem[] = [
  createFallbackItem(1, 'Início', '/', 'bi-house'),
  createFallbackItem(50, 'Hub do Cliente', '/hub/dashboard', 'bi-headset', [
    { ...createFallbackItem(500, 'Painel', '/hub/dashboard', 'bi-speedometer2'), level: 1 },
    { ...createFallbackItem(501, 'Demandas', '/hub/tickets', 'bi-ticket'), level: 1 },
    { ...createFallbackItem(502, 'Notas Fiscais', '/hub/invoices', 'bi-receipt'), level: 1 },
    { ...createFallbackItem(503, 'Contratos', '/hub/contracts', 'bi-file-earmark-text'), level: 1 },
    { ...createFallbackItem(504, 'Horas', '/hub/hours', 'bi-clock-history'), level: 1 },
    { ...createFallbackItem(505, 'Consumo', '/hub/consumption', 'bi-bar-chart'), level: 1 },
    { ...createFallbackItem(506, 'Documentos', '/hub/documents', 'bi-folder2-open'), level: 1 },
  ]),
  createFallbackItem(12, 'Avaliações', '/quiz', 'bi-patch-question', [
    { ...createFallbackItem(121, 'Listar Avaliações', '/quiz', 'bi-list'), level: 1 },
    { ...createFallbackItem(122, 'Nova Avaliação', '/quiz/create', 'bi-plus'), level: 1 },
    { ...createFallbackItem(123, 'Execuções', '/quiz/executions', 'bi-play-circle'), level: 1 },
    { ...createFallbackItem(124, 'Respostas', '/quiz/answers', 'bi-check2-square'), level: 1 },
    { ...createFallbackItem(125, 'Relatórios', '/quiz/reports', 'bi-graph-up'), level: 1 },
    { ...createFallbackItem(126, 'Configurações', '/quiz/config', 'bi-gear'), level: 1 },
  ]),
  createFallbackItem(4, 'Pessoas', '/person', 'bi-people', [
    { ...createFallbackItem(41, 'Listar Pessoas', '/person', 'bi-list'), level: 1 },
    { ...createFallbackItem(43, 'Nova Pessoa', '/person/create', 'bi-plus'), level: 1 },
    { ...createFallbackItem(46, 'Logins', '/person/logins', 'bi-key'), level: 1 },
    { ...createFallbackItem(47, 'Perfis de Acesso', '/person/access', 'bi-shield'), level: 1 },
    { ...createFallbackItem(48, 'Grupos de Cliente', '/person/client-groups', 'bi-people'), level: 1 },
    { ...createFallbackItem(49, 'Atributos', '/person/attributes', 'bi-tags'), level: 1 },
    { ...createFallbackItem(42, 'Geolocalização', '/person/latlng', 'bi-geo-alt'), level: 1 },
    { ...createFallbackItem(44, 'Empresa', '/organization/company', 'bi-building'), level: 1 },
    { ...createFallbackItem(45, 'Organograma', '/organization/organogram', 'bi-diagram-3'), level: 1 },
  ]),
  createFallbackItem(3, 'Conteúdo', '/content', 'bi-file-text'),
  createFallbackItem(2, 'Campanhas', '/campaign', 'bi-flag', [
    { ...createFallbackItem(21, 'Listar Campanhas', '/campaign', 'bi-list'), level: 1 },
    { ...createFallbackItem(22, 'Nova Campanha', '/campaign/create', 'bi-plus'), level: 1 },
    { ...createFallbackItem(23, 'Estornar Lote', '/campaign/score-lots/reversal', 'bi-arrow-counterclockwise'), level: 1 },
    { ...createFallbackItem(24, 'Gerar Creditos', '/campaign/score-lots/generate-credits', 'bi-coin'), level: 1 },
    { ...createFallbackItem(507, 'Gerar Resultados', '/campaign/score-lots/generate-results', 'bi-calculator'), level: 1 },
  ]),
  createFallbackItem(25, 'Comunicação', '/comunicacao', 'bi-megaphone'),
  createFallbackItem(5, 'Recursos', '/resource', 'bi-building', [
    { ...createFallbackItem(51, 'Dashboard', '/resource/dashboard', 'bi-speedometer2'), level: 1 },
    { ...createFallbackItem(52, 'Listar Recursos', '/resource/list', 'bi-list'), level: 1 },
    { ...createFallbackItem(55, 'Novo Recurso', '/resource/create', 'bi-plus'), level: 1 },
    { ...createFallbackItem(56, 'Check-ins', '/resource/checkins', 'bi-geo-alt'), level: 1 },
    { ...createFallbackItem(57, 'Uploads', '/resource/uploads', 'bi-cloud-upload'), level: 1 },
    { ...createFallbackItem(53, 'Campos', '/resource/attributes', 'bi-input-cursor-text'), level: 1 },
    { ...createFallbackItem(54, 'Mapa', '/resource/map-report', 'bi-map'), level: 1 },
  ]),
  // [TH-56761] Telas internas do SAC (categorias/SLA/métricas/insights/canais/
  // prioridades/status) viraram parte da SPA @tmf/service-desk-front em
  // /service-desk — não há mais rotas /sac/* dedicadas pra elas. Mantemos só
  // os destinos com rota real. [TMF9-58757/58758] rota canônica migrou de
  // /sac para /service-desk (/sac vira redirect de compatibilidade).
  createFallbackItem(9, 'SAC', '/service-desk', 'bi-headset', [
    { ...createFallbackItem(91, 'Atendimento', '/service-desk', 'bi-headset'), level: 1 },
  ]),
  createFallbackItem(30, 'Merchandising', '/merchandising', 'bi-shop-window', [
    { ...createFallbackItem(301, 'Check-ins', '/resource/checkins', 'bi-geo-alt'), level: 1 },
    { ...createFallbackItem(302, 'Mapa Check-ins', '/checkin/map', 'bi-map'), level: 1 },
  ]),
  createFallbackItem(25, 'Importações', '/import', 'bi-cloud-upload', [
    { ...createFallbackItem(251, 'Upload', '/import/upload', 'bi-upload'), level: 1 },
    { ...createFallbackItem(252, 'Arquivos', '/import/files', 'bi-files'), level: 1 },
    { ...createFallbackItem(253, 'Logs', '/import/logs', 'bi-journal-text'), level: 1 },
  ]),
  createFallbackItem(19, 'Sorteios', '/drawing', 'bi-dice-5', [
    { ...createFallbackItem(191, 'Resultados', '/drawing/results', 'bi-trophy'), level: 1 },
  ]),
  createFallbackItem(7, 'Loja', '/store', 'bi-shop'),
  createFallbackItem(8, 'Orçamento', '/budget', 'bi-wallet2', BUDGET_CHILDREN),
  createFallbackItem(17, 'Ranking', '/ranking', 'bi-trophy', [
    { ...createFallbackItem(173, 'Gerar Ranking', '/ranking/generate', 'bi-lightning'), level: 1 },
    { ...createFallbackItem(174, 'Jobs', '/ranking/jobs', 'bi-gear'), level: 1 },
  ]),
  createFallbackItem(16, 'Relatórios', '/reports', 'bi-graph-up', [
    { ...createFallbackItem(162, 'Log de Acesso', '/reports/access-log', 'bi-clock-history'), level: 1 },
    { ...createFallbackItem(163, 'Vendas', '/reports/sales', 'bi-cart'), level: 1 },
    { ...createFallbackItem(164, 'Dados de Pessoa', '/reports/person-data', 'bi-person-lines-fill'), level: 1 },
    { ...createFallbackItem(165, 'Orçamento', '/reports/budget', 'bi-wallet'), level: 1 },
    { ...createFallbackItem(166, 'Produtos', '/reports/products', 'bi-box-seam'), level: 1 },
  ]),
  createFallbackItem(26, 'Segmentação', '/segmentation', 'bi-diagram-2', [
    { ...createFallbackItem(261, 'Regiões', '/segmentation/regions', 'bi-geo'), level: 1 },
    { ...createFallbackItem(262, 'Canais', '/segmentation/segments', 'bi-filter'), level: 1 },
  ]),
  createFallbackItem(28, 'Arquivos', '/files', 'bi-folder2-open'),
  // [TMF9-53668] "Funções" movido para dentro de Configurações.
  // Functions agora rodam exclusivamente no Hub — a UI é apenas configuração.
  createFallbackItem(13, 'Configurações', '/settings', 'bi-gear', [
    { ...createFallbackItem(131, 'ETL', '/settings/etl', 'bi-database-gear'), level: 1 },
    { ...createFallbackItem(132, 'Menus', '/settings/menus', 'bi-list'), level: 1 },
    { ...createFallbackItem(133, 'Perfis de Acesso', '/settings/access/profiles', 'bi-shield'), level: 1 },
    { ...createFallbackItem(134, 'Moedas', '/settings/currencies', 'bi-currency-exchange'), level: 1 },
    { ...createFallbackItem(135, 'Temas', '/settings/themes', 'bi-palette'), level: 1 },
    { ...createFallbackItem(136, 'Idiomas', '/settings/languages', 'bi-translate'), level: 1 },
    { ...createFallbackItem(137, 'Funções', '/function', 'bi-code-slash'), level: 1 },
  ]),
  createFallbackItem(11, 'Integrações', '/integrations', 'bi-plug', []),
];

/**
 * Garante que itens de budget tenham sub-itens completos.
 * Mescla os sub-itens do fallback se estiverem faltando.
 */
const ensureBudgetChildren = (items: NavMenuItem[]): NavMenuItem[] => {
  return items.map((item) => {
    // Apenas o item raiz de Orçamento (path EXATO /budget) recebe BUDGET_CHILDREN.
    // Não usar startsWith — sub-itens como /budget/create herdariam os mesmos
    // children e gerariam um nível extra no menu.
    if (item.path === '/budget') {
      if (!item.children || item.children.length < BUDGET_CHILDREN.length) {
        return {
          ...item,
          isGroup: true,
          children: BUDGET_CHILDREN,
        };
      }
      return item;
    }
    // Recursar em outros galhos (não em /budget/*) para não re-injetar
    if (item.children && item.children.length > 0 && !item.path?.startsWith('/budget/')) {
      return {
        ...item,
        children: ensureBudgetChildren(item.children),
      };
    }
    return item;
  });
};

interface DynamicSidebarProps {
  /**
   * Custom menu type code to load.
   * Default is 'menu_principal'.
   */
  menuTypeCode?: string;

  /**
   * Whether to show loading spinner.
   * Default is true.
   */
  showLoading?: boolean;

  /**
   * Callback when menu item is clicked.
   */
  onItemClick?: (item: NavMenuItem) => void;

  /**
   * Additional CSS classes.
   */
  className?: string;
}

/**
 * Dynamic Sidebar component.
 *
 * Loads navigation menu from database via Python API.
 */
export function DynamicSidebar({
   
  menuTypeCode: _menuTypeCode = 'menu_principal',
  showLoading = true,
  onItemClick,
  className,
}: DynamicSidebarProps) {
  const { sidebarOpen, sidebarCollapsed, toggleSidebarCollapsed, setSidebarOpen } = useUIStore();
  const { theme } = useTheme();
  const { user } = useAuth();

  // [TMF9-54372] Considera superadmin tanto via flag explícita do backend
  // (is_superuser → isSuperuser) quanto pela presença das roles admin/
  // superadmin (compatibilidade com sessões antes do bump de schema).
  const isSuperuser =
    user?.isSuperuser ??
    user?.roles?.some((role) => SUPERADMIN_ROLES.includes(role)) ??
    false;

  // Cor do sidebar vinda do tema (configurável por cliente)
  // Fallbacks alinhados com tokens semânticos: gray-100 (light) [TMF9-53595]
  const primaryColor = theme?.colors?.sidebarBackground || '#f3f4f6';
  // Cor de fundo do menu (menu_bg) — independente do sidebar
  const menuBgColor = theme?.menus?.main?.background || primaryColor;

  // ESC não deve afetar a visibilidade do menu principal (TMF9-53761).
  // Em mobile, o usuário fecha a sidebar pelo overlay/backdrop ou pelo
  // botão hamburger do header.

  // Fetch menu from API
  const {
    data: menuData,
    isLoading,
    error,
  } = useSidebarMenu();

  // Get menu items from API, fallback to static items if empty or error.
  // [TMF9-54372] Após montar o menu (API ou fallback), oculta itens marcados
  // como superuser-only (ex: "Configurações") quando o usuário não é
  // superadmin. Filtragem é defesa em profundidade — o backend rejeita as
  // rotas correspondentes com 403.
  const menuItems = useMemo(() => {
    const apiItems = menuData?.navItems ?? [];
    // Use fallback if API returns empty or on error
    const baseItems =
      apiItems.length === 0 || error
        ? FALLBACK_MENU_ITEMS
        : ensureBudgetChildren(apiItems);
    return filterSuperuserOnly(baseItems, isSuperuser);
  }, [menuData?.navItems, error, isSuperuser]);

  // Resolve a single active menu id from the tree + current pathname.
  // Garante que apenas um item fica destacado mesmo com paths duplicados no
  // menu vindo do backend (TMF9-53667).
  const location = useLocation();
  const activeMenuValue = useMemo(() => {
    const activeId = findActiveMenuId(menuItems, location.pathname);
    const ancestorIds = findAncestorIds(menuItems, activeId);
    return { activeId, ancestorIds };
  }, [menuItems, location.pathname]);

  // Handle item click
  const handleItemClick = useCallback(
    (item: NavMenuItem) => {
      onItemClick?.(item);
    },
    [onItemClick]
  );

  // Don't render if sidebar is closed
  if (!sidebarOpen) {
    return null;
  }

  return (
    <ActiveMenuContext.Provider value={activeMenuValue}>
      {/* Overlay para mobile — fecha o sidebar ao tocar fora */}
      {sidebarOpen && !sidebarCollapsed && (
        <div
          className="fixed inset-0 z-30 bg-black/40 sm:hidden"
          onClick={() => setSidebarOpen?.(false)}
          aria-hidden="true"
        />
      )}
    <aside
      className={clsx(
        // [TMF9-55435] borderRight removido — separação visual agora vem do
        // contraste de background (menuBgColor) com o conteúdo + shadow-sm-right
        // (sombra suave à direita) em vez de linha "dura" de 1px.
        'fixed left-0 top-16 h-[calc(100vh-4rem)] transition-all duration-300 z-40',
        'shadow-[1px_0_2px_0_rgba(0,0,0,0.05)] dark:shadow-[1px_0_4px_0_rgba(0,0,0,0.4)]',
        sidebarCollapsed ? 'w-16' : 'w-72',
        className
      )}
      style={{
        backgroundColor: menuBgColor,
        fontSize: '0.818em',
      }}
    >
      {/* Collapse Toggle Button — hover usa gray-200 light / gray-700 dark [TMF9-53595] */}
      <button
        onClick={toggleSidebarCollapsed}
        className="absolute -right-3 top-4 z-50 w-6 h-6 bg-card border-2 rounded-full flex items-center justify-center hover:bg-muted focus:outline-none focus:ring-2 transition-colors"
        style={{ borderColor: primaryColor }}
        aria-label={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
      >
        <svg
          className={clsx(
            'w-4 h-4 text-muted-foreground transition-transform',
            sidebarCollapsed && 'rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>

      {/* Navigation — usa menu_bg para fundo do menu */}
      <nav
        className="p-4 overflow-y-auto h-full scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20 hover:scrollbar-thumb-white/30"
        role="navigation"
        aria-label="Menu principal"
        style={{
          backgroundColor: menuBgColor,
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.2) transparent',
        }}
      >
        {/* Loading State */}
        {isLoading && showLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
            {!sidebarCollapsed && (
              <span className="ml-2 text-sm text-white/70">Carregando...</span>
            )}
          </div>
        )}

        {/* Menu Items (uses fallback automatically on error/empty) */}
        {!isLoading && (
          <ul className="space-y-1" role="menu">
            {menuItems.map((item) => (
              <MenuItem
                key={item.id}
                item={item}
                collapsed={sidebarCollapsed}
                onItemClick={handleItemClick}
              />
            ))}
          </ul>
        )}

        {/* Back to TMF7 link */}
        <Tmf7BackLink collapsed={sidebarCollapsed} />

      </nav>

    </aside>
    </ActiveMenuContext.Provider>
  );
}

/**
 * Link para voltar ao TMF7 (sistema anterior).
 * Só renderiza se TMF7_BASE_URL estiver configurado no tenant.
 */
function Tmf7BackLink({ collapsed }: { collapsed: boolean }) {
  const tmf7Url = useClientSetting<string>('tmf7_base_url', '');

  if (!tmf7Url) return null;

  return (
    <div className="mt-4 pt-4 border-t border-white/10">
      <a
        href={tmf7Url}
        className={clsx(
          'flex items-center gap-3 px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors text-sm',
          collapsed && 'justify-center'
        )}
        title="Voltar ao sistema anterior"
      >
        <svg
          className="w-4 h-4 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        {!collapsed && <span>Sistema Anterior</span>}
      </a>
    </div>
  );
}

export default DynamicSidebar;
